import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import {
  NOTIFICATION_GATE_CATEGORIES,
  useContactNotificationGates,
  useUpdateContactNotificationGates,
} from './hooks/useContactNotificationGates';

/**
 * NotificationSettingsTab — lets assigned staff decide, per category, whether
 * THIS client receives notifications. Toggling a category off suppresses that
 * category's notifications for this client (in addition to the client's own
 * preferences). A category with no gate row is delivered by default.
 */
export function NotificationSettingsTab({ contactId }: { contactId: string }) {
  const { toast } = useToast();
  const { data: gates, isLoading } = useContactNotificationGates(contactId);
  const updateGates = useUpdateContactNotificationGates(contactId);

  // A category is "enabled" (client receives it) when it is NOT gated/disabled.
  const isEnabled = (category: string) => !(gates?.[category] === true);

  const handleToggle = (category: string, enabled: boolean) => {
    // enabled -> disabled:false (deliver); disabled -> the client stops getting it.
    updateGates.mutate(
      { [category]: !enabled },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: enabled ? 'Notifications enabled' : 'Notifications disabled',
          }),
        onError: () => toast({ variant: 'error', title: 'Could not update notification settings' }),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Notification Settings"
        description="Control which notifications this client receives. Turn a category off to stop sending those notifications to this client."
      />
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          </div>
        ) : (
          <ul className="divide-y divide-border dark:divide-border-dark">
            {NOTIFICATION_GATE_CATEGORIES.map((cat) => {
              const enabled = isEnabled(cat.key);
              return (
                <li key={cat.key} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {cat.label}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {cat.description}
                    </p>
                  </div>
                  <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => handleToggle(cat.key, e.target.checked)}
                      disabled={updateGates.isPending}
                      className="peer sr-only"
                      aria-label={`${enabled ? 'Disable' : 'Enable'} ${cat.label} notifications for this client`}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-brand-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-brand-800" />
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Off = this client will not receive notifications in that category.
        </p>
      </CardContent>
    </Card>
  );
}
